import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.ts';
import { heardAboutLabel } from '@shared/heard-about.ts';
import { Alert, Button, LoadingBlock, Select, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { PLANS, isPlanId } from '@shared/constants/index.ts';
import type { AdminCompanySummary } from '@shared/types/admin.ts';
import { SIGNUP_SOURCE_NONE } from '@shared/traffic-source.ts';

/**
 * The cross-shard company list, read from the DIRECTORY mirror — at most one
 * cron interval stale, which the header says plainly. Row click goes to the
 * live detail view.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
];

const PLAN_OPTIONS = [
  { value: '', label: 'Any plan' },
  { value: 'starter', label: PLANS.starter.name },
  { value: 'pro', label: PLANS.pro.name },
  { value: 'business', label: PLANS.business.name },
];

const SORT_OPTIONS = [
  { value: 'created', label: 'Newest first' },
  { value: 'name', label: 'Name' },
  { value: 'mrr', label: 'MRR' },
  { value: 'trial_ends', label: 'Trial ends' },
  { value: 'period_end', label: 'Renews' },
  { value: 'last_activity', label: 'Last activity' },
];

export function statusBadge(status: string, terminationRequested: boolean) {
  if (terminationRequested) {
    return (
      <span className="badge border-[color:var(--color-danger)] text-[color:var(--color-danger)]">Terminating</span>
    );
  }
  switch (status) {
    case 'active':
      return (
        <span className="badge border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]">
          Active
        </span>
      );
    case 'past_due':
      return (
        <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">Past due</span>
      );
    case 'canceled':
      return <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">Canceled</span>;
    default:
      return <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">Trialing</span>;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

function planName(plan: string | null): string {
  return isPlanId(plan) ? PLANS[plan].name : '—';
}

export default function AdminCompaniesPage() {
  useDocumentTitle('Companies');

  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [sort, setSort] = useState('created');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const companies = useQuery({
    queryKey: ['admin', 'companies', { q: search, status, plan, sort, source, page }],
    queryFn: ({ signal }) =>
      adminApi.listCompanies(
        {
          ...(search ? { q: search } : {}),
          ...(status ? { status } : {}),
          ...(plan ? { plan } : {}),
          ...(source ? { source } : {}),
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const sources = useQuery({
    queryKey: ['admin', 'companies', 'signup-sources'],
    queryFn: ({ signal }) => adminApi.signupSources(signal),
  });

  const totalPages = companies.data ? Math.max(1, Math.ceil(companies.data.total / pageSize)) : 1;

  return (
    <div className="portal-page">
      <WorkPage
        title="Companies"
        description="Every company that has signed up, across all shards. List data refreshes within 15 minutes; the detail view is live."
      >
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(q.trim());
          }}
        >
          <div className="min-w-56 flex-1">
            <TextInput
              label="Search"
              placeholder="Company, owner name, email or phone"
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
          <div className="w-40">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-40">
            <Select
              label="Plan"
              options={PLAN_OPTIONS}
              value={plan}
              onChange={(event) => {
                setPlan(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-44">
            <Select
              label="Sort"
              options={SORT_OPTIONS}
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            />
          </div>
          <div className="pb-4 flex gap-2">
            <Button type="submit" variant="primary">
              Search
            </Button>
            <a
              className="btn btn-secondary"
              href={adminApi.signupExportUrl({
                ...(search ? { q: search } : {}),
                ...(status ? { status } : {}),
                ...(plan ? { plan } : {}),
                ...(source ? { source } : {}),
              })}
            >
              Download CSV
            </a>
          </div>
        </form>

        {sources.isError ? (
          <div className="mb-4">
            <Alert tone="error">
              Signup sources could not be loaded.{' '}
              <Button variant="ghost" onClick={() => void sources.refetch()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : null}

        {sources.data && sources.data.sources.length > 0 ? (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-[color:var(--color-muted)]">Signups by source</span>
            <button
              type="button"
              className={`rounded-full border px-2 py-0.5 ${source === '' ? 'border-[color:var(--color-brand)] font-semibold' : 'border-[color:var(--color-line)]'}`}
              onClick={() => {
                setSource('');
                setPage(1);
              }}
            >
              All
            </button>
            {sources.data.sources.map((row) => (
              <button
                key={row.source}
                type="button"
                className={`rounded-full border px-2 py-0.5 ${source === row.source ? 'border-[color:var(--color-brand)] font-semibold' : 'border-[color:var(--color-line)]'}`}
                onClick={() => {
                  setSource(row.source);
                  setPage(1);
                }}
              >
                {row.source === SIGNUP_SOURCE_NONE ? 'No source' : row.source} ({row.count})
              </button>
            ))}
          </div>
        ) : null}

        {companies.isError ? (
          <Alert tone="error">
            The company list could not be loaded.{' '}
            <Button variant="ghost" onClick={() => void companies.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : companies.isLoading ? (
          <LoadingBlock label="Loading companies…" />
        ) : (companies.data?.items.length ?? 0) === 0 ? (
          <WorkEmpty title="No companies match">
            {search || status || plan || source ? 'Try clearing the filters.' : 'Nobody has signed up yet.'}
          </WorkEmpty>
        ) : (
          <>
            <TableScroll aria-label="Companies" className="card overflow-x-auto p-0">
              {/* Eight columns do not fit a phone, and `w-full` alone pins the
                table to the wrapper so the wrapper never scrolls — the
                columns squeeze instead until every cell breaks mid-word.
                The table takes the width its content asks for; the one
                column a customer can make arbitrarily wide is bounded so a
                long company name cannot decide that width for every row. */}
              <table className="data-table w-full min-w-max text-sm">
                <thead>
                  <tr className="border-b border-[color:var(--color-line)] text-left text-[color:var(--color-muted)]">
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Company
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Plan
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium whitespace-nowrap">
                      MRR
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium whitespace-nowrap">
                      Seats
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Trial ends
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Renews
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      Last active
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(companies.data?.items ?? []).map((company: AdminCompanySummary) => (
                    <tr
                      key={company.id}
                      className="border-b border-[color:var(--color-line)] last:border-b-0 hover:bg-[color:var(--color-surface)]"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/companies/${company.id}`}
                          className="block max-w-[18rem] table-text font-semibold text-[color:var(--color-brand)] hover:underline"
                        >
                          {company.name}
                        </Link>
                        {company.ownerName || company.ownerPhone || company.phone ? (
                          <div className="max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                            {[company.ownerName, company.ownerPhone ?? company.phone].filter(Boolean).join(' · ')}
                          </div>
                        ) : null}
                        <div className="max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                          {company.ownerEmail ?? '—'}
                          {company.signupUtmSource ? ` · ${company.signupUtmSource}` : ''}
                          {company.heardAbout ? ` · ${heardAboutLabel(company.heardAbout)}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {statusBadge(company.billingStatus, company.terminationRequestedAt !== null)}
                        {company.cancelAtPeriodEnd ? (
                          <div className="mt-1 text-xs text-[color:var(--color-warning)]">Cancels at period end</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {planName(company.plan)}
                        {company.billingInterval === 'year' ? (
                          <span className="ml-1 text-xs text-[color:var(--color-muted)]">(yearly)</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                        {company.mrrCents > 0 ? formatCents(company.mrrCents, companies.data?.currencyCode) : '—'}
                        {company.discountPercent ? (
                          <div className="text-xs text-[color:var(--color-muted)]">−{company.discountPercent}%</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{company.seats}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {company.billingStatus === 'trialing' ? formatDate(company.trialEndsAt) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(company.billingPeriodEnd)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(company.lastActivityAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>

            {totalPages > 1 ? (
              <nav className="mt-4 flex items-center justify-between">
                <Button disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                  Previous
                </Button>
                <span className="text-sm text-[color:var(--color-muted)]">
                  Page {page} of {totalPages} · {companies.data?.total ?? 0} companies
                </span>
                <Button disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
                  Next
                </Button>
              </nav>
            ) : null}
          </>
        )}
      </WorkPage>
    </div>
  );
}
